import MessageWithDbComponent
    from "@/screens/app/project/universal-app-builder-layout/universal-app-builder/chat-screen/message-with-db/MessageWithDbComponent.tsx";
import { motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch, useSelector } from "react-redux";
import UserAvatarComponent from "@/components/common-components/user-avatar/UserAvatarComponent.tsx";
import { ImageConfig } from "@/constants";
import { setCurrentPrompt } from "@/store/actions/auth.action.ts";
import { IRootReducerState } from "@/store/reducers";
import './ChatScreenComponent.scss';
import { useChatState } from '@/hooks/useChatState';
import { useChatApi } from '@/hooks/useChatApi';
import { useChatSocket } from "@/hooks/useChatSocket.ts";
import { useSocket } from "@/services/hooks/useSocket";
import { useVirtualizedMessages } from '@/hooks/useVirtualizedMessages.ts'; // Import the new hook
import { CommonService } from "@/services";
import { getUsersTokensUtilization } from "@/store/actions/meta.action.ts";
import CustomInputComponent from "@/components/ui/custom-input/CustomInputComponent.tsx";
import ProjectAssetsComponent
    from "@/screens/app/project/universal-app-builder-layout/universal-app-builder/project-assets/ProjectAssetsComponent.tsx";
import { MoveDownIcon, Clock, Gift } from "lucide-react";
import moment from "moment";
import { Button } from "@/components/ui/button";
import ChatScreenHeaderComponent from "./chat-screen-header/ChatScreenHeaderComponent.tsx";
import { AppType } from "@/models/project.model.ts";
import { setPmConversationSuccessful, setConversationStep, ConversationStep, setIframeUrl } from "@/store/actions/projectdata.action.ts";
import ConversationSummaryApprovalDialogComponent from "@/components/common-components/conversation-summary-approval-dialog/ConversationSummaryApprovalDialogComponent.tsx";
import PmAgentDialogComponent from "@/components/common-components/pm-agent-dialog/PmAgentDialogComponent.tsx";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { useAdvancedMode } from "@/hooks/useAdvancedMode.ts";
import { ProcessItem } from "@/components/common-components/advanced-mode-process-loader/AdvancedModeProcessLoaderComponent";
import { ChatErrorIndicatorComponent } from "./chat-error-indicator/ChatErrorIndicatorComponent";

// Conversation step configuration
const CONVERSATION_STEP_CONFIG = {
    'requirement-gathering': {
        title: "Ready to Build Your App?",
        description: "Review the conversation summary below and approve to start building your application.",
        buttonText: "Start App Generation",
        loadingButtonText: "Starting App Generation...",
        streamingButtonText: "Generating Prompt...",
        cancelButtonText: "Cancel",
        promptLabel: "Conversation Summary:",
        placeholderText: "Edit your conversation summary...",
        streamingPlaceholderText: "Generating prompt..."
    },
    'product-manager': {
        title: "Execute Your Product Plan",
        description: "Review the product management plan below and execute it to generate your application.",
        buttonText: "Execute Plan & Generate App",
        loadingButtonText: "Executing Plan...",
        streamingButtonText: "Generating Plan...",
        cancelButtonText: "Cancel",
        promptLabel: "Product Management Plan:",
        placeholderText: "Edit your product plan...",
        streamingPlaceholderText: "Generating plan..."
    },
    'prompt-enhancement': {
        title: "Enhance Your Prompt",
        description: "Review and enhance the generated prompt below to optimize your application generation.",
        buttonText: "Enhance & Generate App",
        loadingButtonText: "Enhancing Prompt...",
        streamingButtonText: "Enhancing Prompt...",
        cancelButtonText: "Cancel",
        promptLabel: "Enhanced Prompt:",
        placeholderText: "Edit your enhanced prompt...",
        streamingPlaceholderText: "Enhancing prompt..."
    }
} as const;

// Helper function to get configuration based on conversation step
const getConversationStepConfig = (step: ConversationStep) => {
    return CONVERSATION_STEP_CONFIG[step] || CONVERSATION_STEP_CONFIG['requirement-gathering'];
};

interface ChatScreenProps {
    projectId: string;
    appType: AppType;
    onExitChat: () => void;
    onCollapseChat: () => void;
}

const ChatScreen = ({ projectId, appType, onExitChat, onCollapseChat }: ChatScreenProps) => {
    const [chatInput, setChatInput] = useState<string>('');
    const chatHistoryRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();
    const { currentPrompt, user: currentUser } = useSelector((state: IRootReducerState) => state.auth);
    const pmConversationSuccessful = useSelector((state: IRootReducerState) => state.projectData.pmConversationSuccessful);
    const conversationStep = useSelector((state: IRootReducerState) => state.projectData.conversationStep);
    const conversationSummaryPrompt = useSelector((state: IRootReducerState) => state.projectData.conversationSummaryPrompt);
    const iframeUrl = useSelector((state: IRootReducerState) => state.projectData.iframeUrl);
    const messageRefs = useRef<HTMLDivElement[]>([]);
    const convertingProjectId = useSelector((state: IRootReducerState) => state.projectData.convertingProjectId)
    const subscriptions = useSelector((state: IRootReducerState) => state.meta.subscriptions);
    const usersBalance = useSelector((state: IRootReducerState) => state.meta.usersBalance);
    const [selectedAssets, setSelectedAssets] = useState<any[]>([]);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [lastMessageHeight, setLastMessageHeight] = useState(0);
    const [showScrollIndicator, setShowScrollIndicator] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [showApprovalDialog, setShowApprovalDialog] = useState(false);
    const [pendingConversationPrompt, setPendingConversationPrompt] = useState<string | null>(null);
    const [showPmAgentDialog, setShowPmAgentDialog] = useState(false);
    const [showPmAgentTooltip, setShowPmAgentTooltip] = useState(false);
    const [hasSeenPmAgentTooltip, setHasSeenPmAgentTooltip] = useState(false);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const processItemsRef = useRef<Map<string, ProcessItem>>(new Map()); // Use Map to store items by ID
    const previewUrlResolverRef = useRef<{ resolve: (url: string) => void; reject: (error: any) => void } | null>(null);
    const isCurrentPromptProcessed = useRef(false);
    const [isDocumentHidden, setIsDocumentHidden] = useState(document.hidden);
    const [isAdvancedModeEnabled, setIsAdvancedModeEnabled] = useState(false);
    const isMobile = useIsMobile();

    // Determine if user is on free plan with 0 balance
    const userSubscription = currentUser?.subscription;
    const currentPlanId = userSubscription?.plan_id;
    const currentPlanDetails = Array.isArray(subscriptions)
        ? (subscriptions as any[]).find((sub: any) => sub.id === currentPlanId)
        : null;
    const currentPlanName = currentPlanDetails?.plan_name || userSubscription?.plan_name || "Free";
    const isFreeUser = !currentPlanId || currentPlanName.toLowerCase() === "free" || !userSubscription;
    const availableBalance = usersBalance?.user_remaining_budget || 0;
    const usersBalanceLoading = useSelector((state: IRootReducerState) => state.meta.usersBalanceLoading);
    // Only show free credits note after balance is loaded and conditions are met
    const showFreeCreditsNote = !usersBalanceLoading && isFreeUser && availableBalance <= 0.2;

    // Calculate next credit refresh time (UTC 00:00) in user's local timezone
    const nextCreditRefreshTime = useMemo(() => {
        const nextMidnightUTC = moment.utc().endOf('day').add(1, 'second');
        return nextMidnightUTC.local().format('h:mm A');
    }, []);

    // Callback to receive advanced mode state from CustomInputComponent
    const handleAdvancedModeChange = useCallback((enabled: boolean) => {
        setIsAdvancedModeEnabled(enabled);
    }, []);

    // Callback to receive session ID from CustomInputComponent
    const handleSessionIdChange = useCallback((id: string | null) => {
        setSessionId(id);
    }, []);

    const {
        messages,
        isChatInProgress,
        setIsChatInProgress,
        addUserMessage,
        addLoadingMessage,
        updateLoadingMessage,
        handleErrorMessage,
        handleRetryMessage,
    } = useChatState();

    // Initialize virtualization
    const {
        visibleMessages,
        startIndex,
        topPadding,
        bottomPadding,
        handleScroll: handleVirtualScroll,
        scrollToBottom: virtualScrollToBottom,
    } = useVirtualizedMessages({
        messages,
        windowSize: 30,
        chatHistoryRef
    });

    useChatSocket(updateLoadingMessage, projectId, handleErrorMessage, false)

    const {
        sendChatMessage,
        convertAppChatMessage
    } = useChatApi(
        projectId,
        appType,
        () => setIsChatInProgress(true),
        () => setIsChatInProgress(false),
        handleErrorMessage
    );

    const { executeAdvancedMode } = useAdvancedMode();

    // Socket subscription for preview events (for advanced mode container wake-up)
    const { events: previewEvents, clearEvent: clearPreviewEvent } = useSocket({
        eventTypes: [
            'preview.started',
            'preview.delta',
            'preview.success',
            'preview.error'
        ],
        autoConnect: true,
        persist: true,
        projectId: projectId || "",
        logTag: 'ChatScreen-Preview',
        debug: false,
    });

    // Handle preview socket events for advanced mode container wake-up
    useEffect(() => {
        if (previewEvents['preview.success']) {
            const successEvent = previewEvents['preview.success'];
            const url = successEvent?.event_meta?.preview_url;

            if (url && previewUrlResolverRef.current) {
                dispatch(setIframeUrl(url));
                previewUrlResolverRef.current.resolve(url);
                previewUrlResolverRef.current = null;
            }
            clearPreviewEvent('preview.success');
        }

        if (previewEvents['preview.error']) {
            const errorEvent = previewEvents['preview.error'];

            if (previewUrlResolverRef.current) {
                previewUrlResolverRef.current.reject(new Error(errorEvent?.error || 'Preview generation failed'));
                previewUrlResolverRef.current = null;
            }
            clearPreviewEvent('preview.error');
        }
    }, [previewEvents, clearPreviewEvent]);

    // Function to wait for preview URL from socket
    const waitForPreviewUrl = useCallback((): Promise<string> => {
        return new Promise((resolve, reject) => {
            // Set a timeout for waiting
            const timeout = setTimeout(() => {
                if (previewUrlResolverRef.current) {
                    previewUrlResolverRef.current.reject(new Error('Container startup timeout'));
                    previewUrlResolverRef.current = null;
                }
            }, 120000); // 2 minute timeout

            previewUrlResolverRef.current = {
                resolve: (url: string) => {
                    clearTimeout(timeout);
                    resolve(url);
                },
                reject: (error: any) => {
                    clearTimeout(timeout);
                    reject(error);
                }
            };
        });
    }, []);

    // Use the virtualized scroll to bottom
    const scrollToBottom = useCallback(() => {
        virtualScrollToBottom();
    }, [virtualScrollToBottom]);

    const calculateLastMessageHeight = useCallback(() => {
        if (!chatHistoryRef.current || !messageRefs.current.length) return;

        setTimeout(() => {
            const chatHistoryHeight = chatHistoryRef.current?.clientHeight || 0;
            let previousUserMessageHeight = 0;

            for (let i = messageRefs.current.length - 1; i >= 0; i--) {
                const message = messageRefs.current[i];
                if (message && message.classList.contains('user')) {
                    previousUserMessageHeight = message.offsetHeight;
                    break;
                }
            }
            if (previousUserMessageHeight < chatHistoryHeight) {
                setLastMessageHeight(chatHistoryHeight - previousUserMessageHeight - 30);
            }
            else {
                setLastMessageHeight(0)
            }
            scrollToBottom();
        }, 100);
    }, [scrollToBottom]);

    useEffect(() => {
        const sub = CommonService._communications.scrollToBottomOfChatSubject.subscribe((data: any) => {
            if (data) {
                scrollToBottom();
            }
        })
        return () => {
            sub.unsubscribe();
        }
    }, [scrollToBottom]);

    const handleSendMessage = useCallback(async (input: string, imageToSend: File | null, selectedNetworkErrorsParam?: any[], chatMode?: boolean) => {
        if (!input.trim() && !imageToSend) return;

        setChatInput("");
        setImageFile(null);
        setIsChatInProgress(true);
        let chatInputWithAssets = input;

        if (selectedAssets && selectedAssets.length > 0) {
            const assetUrls = selectedAssets
                .filter((asset: any) => asset && asset.url)
                .map((asset: any) => asset.url)
                .filter((url: any) => url && url.trim())
                .join(",");

            if (assetUrls) {
                chatInputWithAssets = `${input}\n<assets>[${assetUrls}]</assets>`;
            }
        }

        // Add network errors to the message if any were selected
        if (selectedNetworkErrorsParam && selectedNetworkErrorsParam.length > 0) {
            const networkErrorsText = selectedNetworkErrorsParam.map((netErr: any) => {
                let errorText = `Network Error: ${netErr.method} ${netErr.url}\n`;
                if (netErr.status) {
                    errorText += `Status: ${netErr.status}\n`;
                }
                if (netErr.error) {
                    errorText += `Error: ${netErr.error}\n`;
                }
                if (netErr.response) {
                    const responseStr = typeof netErr.response === 'string'
                        ? netErr.response
                        : JSON.stringify(netErr.response, null, 2);
                    errorText += `Response: ${responseStr}`;
                }
                return errorText;
            }).join('\n\n');

            chatInputWithAssets = `${chatInputWithAssets}\n\n<network_errors>${networkErrorsText}</network_errors>`;
        }

        addUserMessage(chatInputWithAssets, imageToSend);
        setSelectedAssets([])
        addLoadingMessage();
        calculateLastMessageHeight();

        // If advanced mode is enabled, always use advanced mode (even in error cases)
        if (isAdvancedModeEnabled) {
            // Use advanced mode execution
            CommonService.handleMixpanelTrack("advanced_mode_message_sent", {
                project_id: projectId,
                app_type: appType,
                message: chatInputWithAssets
            });

            // Use iframeUrl if available, otherwise use empty string (advanced mode will handle the error)
            processItemsRef.current.clear(); // Clear previous items

            await executeAdvancedMode({
                projectId,
                message: chatInputWithAssets,
                previewUrl: iframeUrl || "",
                waitForPreviewUrl,
                sessionId: sessionId || undefined,
                onMessage: (message: any, isStreaming?: boolean) => {
                    // Include advanced mode flag and current items in message extra
                    const messageWithItems = {
                        ...message,
                        extra: {
                            ...message.extra,
                            isAdvancedMode: true,
                            processItems: Array.from(processItemsRef.current.values())
                        }
                    };
                    updateLoadingMessage(messageWithItems, isStreaming);
                },
                onError: (errorMessage?: string) => {
                    handleErrorMessage(errorMessage);
                },
                onComplete: () => {
                    setIsChatInProgress(false);
                    scrollToBottom();
                    dispatch(getUsersTokensUtilization());
                },
                onProcessItem: (item: ProcessItem) => {
                    // Update or add item in the map (this handles both new items and streaming updates)
                    processItemsRef.current.set(item.id, item);
                    // Update the loading message with the current items array
                    updateLoadingMessage({
                        extra: {
                            processItems: Array.from(processItemsRef.current.values()),
                            isAdvancedMode: true
                        }
                    }, false);
                }
            });
        } else {
            // Use regular chat message
            await sendChatMessage(chatInputWithAssets, imageToSend, chatMode);
        }

    }, [addUserMessage, addLoadingMessage, sendChatMessage, setIsChatInProgress, selectedAssets, calculateLastMessageHeight, isAdvancedModeEnabled, iframeUrl, projectId, appType, executeAdvancedMode, updateLoadingMessage, handleErrorMessage, scrollToBottom, dispatch, sessionId, waitForPreviewUrl]);

    const handleRetryChatMessage = useCallback(
        async () => {
            const lastUserMessage = [...messages].reverse().find((message: any) => message.role === 'user');
            if (lastUserMessage) {
                handleRetryMessage();
                setIsChatInProgress(true);
                addLoadingMessage();
                calculateLastMessageHeight();

                // If advanced mode is enabled, always use advanced mode (even in error cases)
                if (isAdvancedModeEnabled) {
                    // Use advanced mode execution for retry
                    CommonService.handleMixpanelTrack("advanced_mode_message_retried", {
                        project_id: projectId,
                        app_type: appType,
                        message: lastUserMessage.message
                    });

                    // Use iframeUrl if available, otherwise use empty string (advanced mode will handle the error)
                    processItemsRef.current.clear(); // Clear previous items

                    await executeAdvancedMode({
                        projectId,
                        message: lastUserMessage.message,
                        previewUrl: iframeUrl || "",
                        waitForPreviewUrl,
                        sessionId: sessionId || undefined,
                        onMessage: (message: any, isStreaming?: boolean) => {
                            // Include advanced mode flag and current items in message extra
                            const messageWithItems = {
                                ...message,
                                extra: {
                                    ...message.extra,
                                    isAdvancedMode: true,
                                    processItems: Array.from(processItemsRef.current.values())
                                }
                            };
                            updateLoadingMessage(messageWithItems, isStreaming);
                        },
                        onError: (errorMessage?: string) => {
                            handleErrorMessage(errorMessage);
                        },
                        onComplete: () => {
                            setIsChatInProgress(false);
                            scrollToBottom();
                            dispatch(getUsersTokensUtilization());
                        },
                        onProcessItem: (item: ProcessItem) => {
                            // Update or add item in the map (this handles both new items and streaming updates)
                            processItemsRef.current.set(item.id, item);
                            // Update the loading message with the current items array
                            updateLoadingMessage({
                                extra: {
                                    processItems: Array.from(processItemsRef.current.values()),
                                    isAdvancedMode: true
                                }
                            }, false);
                        }
                    });
                } else {
                    // Use regular chat message for retry
                    await sendChatMessage(lastUserMessage?.message, null, false);
                }
            }
        }, [handleRetryMessage, sendChatMessage, messages, isAdvancedModeEnabled, iframeUrl, projectId, appType, executeAdvancedMode, updateLoadingMessage, handleErrorMessage, setIsChatInProgress, addLoadingMessage, calculateLastMessageHeight, scrollToBottom, dispatch, sessionId, waitForPreviewUrl]);

    const handleConvertApp = useCallback(async (projectIdOfConverting: string) => {
        if (isChatInProgress) return;
        const input = "convert this project to nextjs"
        setChatInput("");
        setIsChatInProgress(true);
        addUserMessage(input, null);
        addLoadingMessage();
        CommonService.handleMixpanelTrack("Convert App button clicked", {
            project_id: projectId,
            app_type: appType,
        });
        await convertAppChatMessage(projectIdOfConverting);
    }, [addUserMessage, addLoadingMessage, convertAppChatMessage, setIsChatInProgress, isChatInProgress, projectId, appType])

    // Handle PM conversation prompt approval
    const handleApprovePmConversationPrompt = useCallback(async (editedPrompt?: string) => {
        if (editedPrompt) {
            setShowApprovalDialog(false);
            await handleSendMessage(editedPrompt, null);
            dispatch(setPmConversationSuccessful(false));
            setPendingConversationPrompt(null);
        }
    }, [handleSendMessage, dispatch]);

    // Handle PM conversation prompt cancellation
    const handleCancelPmConversationPrompt = useCallback(() => {
        setShowApprovalDialog(false);
        dispatch(setPmConversationSuccessful(false));
        setPendingConversationPrompt(null);
    }, [dispatch]);

    useEffect(() => {
        if (currentPrompt && !isCurrentPromptProcessed.current) {
            isCurrentPromptProcessed.current = true;
            setChatInput(currentPrompt);
            dispatch(setCurrentPrompt(""));
            CommonService.handleMixpanelTrack("Current Prompt set", {
                project_id: projectId,
                app_type: appType,
            });
        }
    }, [currentPrompt, dispatch, projectId, appType]);

    // Handle PM conversation successful - show dialog
    useEffect(() => {
        if (pmConversationSuccessful) {
            setShowApprovalDialog(true);
        }
    }, [pmConversationSuccessful]);

    // Track document visibility changes
    useEffect(() => {
        const handleVisibilityChange = () => {
            setIsDocumentHidden(document.hidden);
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    const checkScrollPosition = useCallback(() => {
        if (!chatHistoryRef.current) return;
        const { scrollTop, scrollHeight, clientHeight } = chatHistoryRef.current;
        setShowScrollIndicator(scrollHeight - scrollTop - clientHeight > 5);
    }, []);

    // Combined scroll handler for both virtualization and scroll indicator
    const handleCombinedScroll = useCallback(() => {
        handleVirtualScroll();
        checkScrollPosition();
    }, [handleVirtualScroll, checkScrollPosition]);

    useEffect(() => {
        const chatHistory = chatHistoryRef.current;
        if (chatHistory) {
            chatHistory.addEventListener('scroll', handleCombinedScroll);
            return () => chatHistory.removeEventListener('scroll', handleCombinedScroll);
        }
    }, [handleCombinedScroll]);

    useEffect(() => {
        checkScrollPosition();
    }, [visibleMessages, checkScrollPosition]);

    const handleScrollToBottom = useCallback(() => {
        scrollToBottom();
    }, [scrollToBottom]);

    useEffect(() => {
        const sub = CommonService._communications.sendMessageSubject.subscribe((data: any) => {
            if (data?.message) {
                handleSendMessage(data.message, null);
            }
        });
        return () => {
            sub.unsubscribe();
        }
    }, [handleSendMessage]);

    useEffect(() => {
        const sub = CommonService._communications.endCodeStreamerSubject.subscribe((data: any) => {
            if (data) {
                setTimeout(() => {
                    scrollToBottom();
                    dispatch(getUsersTokensUtilization());
                }, 1000);

                if (isDocumentHidden) {
                    // Play notification sound after 3 seconds
                    setTimeout(() => {
                        CommonService._audioNotification.playNotification("success");
                    }, 3000);
                }
            }
        });
        return () => {
            sub.unsubscribe();
        }
    }, [dispatch, scrollToBottom, isDocumentHidden]);

    useEffect(() => {
        if (convertingProjectId) {
            handleConvertApp(convertingProjectId)
        }
    }, [handleConvertApp, convertingProjectId]);

    // Show PM Agent tooltip after user has been in chat for a while
    useEffect(() => {
        if (!hasSeenPmAgentTooltip && messages.length > 2) {
            const timer = setTimeout(() => {
                setShowPmAgentTooltip(true);
                setHasSeenPmAgentTooltip(true);
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [messages.length, hasSeenPmAgentTooltip]);

    // Reset tooltip state when PM agent dialog is opened
    useEffect(() => {
        if (showPmAgentDialog) {
            setHasSeenPmAgentTooltip(true);
            setShowPmAgentTooltip(false);
        }
    }, [showPmAgentDialog]);

    // Auto-hide tooltip after some time
    useEffect(() => {
        if (showPmAgentTooltip) {
            const timer = setTimeout(() => {
                setShowPmAgentTooltip(false);
            }, 8000);

            return () => clearTimeout(timer);
        }
    }, [showPmAgentTooltip]);

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        CommonService.handleMixpanelTrack("Image dropped in chat", {
            project_id: projectId,
            app_type: appType,
        });
        setIsDragOver(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            setImageFile(e.dataTransfer.files[0]);
            e.dataTransfer.clearData();
        }
    };

    useEffect(() => {
        const sub = CommonService._communications.retryChatMessageSubject.subscribe((data: any) => {
            if (data?.retryMessage) {
                handleRetryChatMessage();
            }
        });
        return () => {
            sub.unsubscribe();
        }
    }, [handleRetryChatMessage]);


    return (
        <div className="chat-screen-wrapper">
            <ChatScreenHeaderComponent
                onExitChat={onExitChat}
                projectId={projectId}
                appType={appType}
                onCollapseChat={onCollapseChat}
            />
            <div
                className={`app-chat-container ${isDragOver ? 'drop-zone' : ''} ${selectedAssets && selectedAssets.length > 0 ? 'has-assets' : ''}`}
                style={{ position: 'relative' }}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {isDragOver && <div className="drop-overlay">Drop image to upload</div>}

                <div className="chat-history main-chat-history" ref={chatHistoryRef}>
                    {topPadding > 0 && <div style={{ height: `${topPadding}px` }} />}

                    {visibleMessages?.map((message, index) => {
                        const actualIndex = startIndex + index;
                        const isLastMessage = actualIndex === messages.length - 1;

                        return (
                            <motion.div
                                ref={(el: HTMLDivElement | null) => {
                                    messageRefs.current[actualIndex] = el as HTMLDivElement;
                                }}
                                key={message.id}
                                className={`message ${message.role}`}
                                initial={{ y: 100 }}
                                animate={{ y: 0 }}
                                style={{
                                    whiteSpace: 'pre-wrap',
                                    minHeight: isLastMessage && lastMessageHeight ? `${lastMessageHeight}px` : 'auto'
                                }}
                            >
                                {(message.role === 'agent' || message.role === 'loading')
                                    ? <ImageConfig.ChatSuperEngineerIcon className='mb-1' />
                                    : <UserAvatarComponent />
                                }
                                <motion.div
                                    className={`${message.role}-content`}
                                    initial={{ y: 100 }}
                                    animate={{ y: 0 }}
                                    style={{ whiteSpace: 'pre-wrap' }}
                                >
                                    <MessageWithDbComponent
                                        message={message as any}
                                        isLast={isLastMessage}
                                        projectId={projectId}
                                    />
                                </motion.div>
                            </motion.div>
                        );
                    })}

                    {bottomPadding > 0 && <div style={{ height: `${bottomPadding}px` }} />}
                </div>

                {showScrollIndicator && (
                    <div className="middle-scroll-section" style={{
                        position: 'absolute',
                        bottom: isMobile ? '160px' : '180px',
                        right: '20px',
                        zIndex: 10,
                    }}>
                        <Button
                            onClick={handleScrollToBottom}
                            style={{
                                borderRadius: '50%',
                                justifyContent: 'center',
                                width: '40px',
                                height: '40px',
                            }}
                            variant="secondaryForeground"
                            mixpanelEvent={{
                                name: "chat_scroll_to_bottom_clicked",
                                properties: {
                                    project_id: projectId,
                                    app_type: appType
                                }
                            }}
                        >
                            <MoveDownIcon size={16} color="white" />
                        </Button>
                    </div>
                )}

                {/* Free credits refresh note for free users with 0 balance */}
                {showFreeCreditsNote && (
                    <div className="free-credits-note">
                        <Gift className="free-credits-note__icon" />
                        <span className="free-credits-note__text">
                            Free credits refresh daily at <strong>{nextCreditRefreshTime}</strong> (your local time)
                        </span>
                        <Clock className="free-credits-note__clock" />
                    </div>
                )}

                <CustomInputComponent
                    onSend={handleSendMessage}
                    value={chatInput}
                    onInputChange={setChatInput}
                    isNew={false}
                    loading={isChatInProgress}
                    selectedFiles={selectedAssets}
                    onFileRemove={(index: number) => {
                        setSelectedAssets(selectedAssets.filter((_, i) => i !== index))
                    }}
                    projectId={projectId}
                    imageFile={imageFile}
                    onImageFileChange={setImageFile}
                    onAdvancedModeChange={handleAdvancedModeChange}
                    onSessionIdChange={handleSessionIdChange}
                />
                <ProjectAssetsComponent projectId={projectId} onSelectAssets={(assets: any[]) => setSelectedAssets(assets)}
                    preSelectedAssets={selectedAssets} />

                <ChatErrorIndicatorComponent />
            </div>

            <ConversationSummaryApprovalDialogComponent
                isOpen={showApprovalDialog}
                onClose={handleCancelPmConversationPrompt}
                onApprove={handleApprovePmConversationPrompt}
                prompt={conversationSummaryPrompt || pendingConversationPrompt || ""}
                isLoading={isChatInProgress}
                isEditable={pmConversationSuccessful}
                showEditButton={!pmConversationSuccessful}
                projectId={projectId}
                isPmConversation={pmConversationSuccessful}
                currentConversationStep={conversationStep}
                {...getConversationStepConfig(conversationStep)}
            />

            <PmAgentDialogComponent
                isOpen={showPmAgentDialog}
                onClose={() => setShowPmAgentDialog(false)}
                projectId={projectId}
                appType={appType}
                onExecutePlan={() => {
                    setShowPmAgentDialog(false);
                    setShowApprovalDialog(true);
                    dispatch(setPmConversationSuccessful(true));
                    dispatch(setConversationStep('prompt-enhancement'));
                }}
            />
        </div>
    );
}

export default ChatScreen;